import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import { ErrorBoundary } from 'react-error-boundary';
import { useCurrentUser } from '@/features/auth/hooks/useCurrentUser';

const SignInPage = lazy(() =>
  import('@/features/auth/components/SignInPage').then((m) => ({ default: m.SignInPage }))
);
const SignUpPage = lazy(() =>
  import('@/features/auth/components/SignUpPage').then((m) => ({ default: m.SignUpPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/components/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);
const AppShell = lazy(() =>
  import('@/app/layout/AppShell').then((m) => ({ default: m.AppShell }))
);
const OrdersPage = lazy(() =>
  import('@/features/orders/components/OrdersPage').then((m) => ({ default: m.OrdersPage }))
);
const NotFoundPage = lazy(() =>
  import('@/app/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);
const ErrorScreen = lazy(() =>
  import('@/app/ErrorScreen').then((m) => ({ default: m.ErrorScreen }))
);
const WaitingPage = lazy(() =>
  import('@/app/WaitingPage').then((m) => ({ default: m.WaitingPage }))
);

const RouteSuspense = ({ children }: { children: React.ReactNode }) => (
  <Suspense
    fallback={
      <Center h="100vh">
        <Loader />
      </Center>
    }
  >
    {children}
  </Suspense>
);

const RequireAuth = () => {
  const { isAuthenticated } = useCurrentUser();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }
  return <Outlet />;
};

const RootError = () => <ErrorScreen />;

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary FallbackComponent={ErrorScreen}>
        <Outlet />
      </ErrorBoundary>
    ),
    errorElement: <RootError />,
    children: [
      { index: true, element: <Navigate to="/orders" replace /> },
      {
        path: 'signin',
        element: (
          <RouteSuspense>
            <SignInPage />
          </RouteSuspense>
        ),
      },
      {
        path: 'signup',
        element: (
          <RouteSuspense>
            <SignUpPage />
          </RouteSuspense>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <RouteSuspense>
            <ForgotPasswordPage />
          </RouteSuspense>
        ),
      },
      {
        path: 'waiting',
        element: (
          <RouteSuspense>
            <WaitingPage />
          </RouteSuspense>
        ),
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: (
              <RouteSuspense>
                <AppShell />
              </RouteSuspense>
            ),
            children: [
              {
                path: 'orders',
                element: (
                  <RouteSuspense>
                    <OrdersPage />
                  </RouteSuspense>
                ),
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: (
          <RouteSuspense>
            <NotFoundPage />
          </RouteSuspense>
        ),
      },
    ],
  },
]);
